/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, ReactNode, useContext, useMemo, useReducer } from 'react'

import { initRoutes, RouteType } from 'routes'

import {
	ContextActionTypes,
	FeedbackConfiguration,
	MaterialUIContextType,
	OrganisationConfiguration,
	OrgAssets,
	ShortBusinessAccounts,
	ShortOrganisationMembers,
	StateTypes,
} from './types'

// The Material Dashboard 2 PRO React TSUI Dashboard PRO Material main context
const MaterialUI = createContext<MaterialUIContextType>(null)

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = 'MaterialUIContext'

// Material Dashboard 2 PRO React reducer
function reducer(state: StateTypes, action: ContextActionTypes): StateTypes {
	switch (action.type) {
		case 'MINI_SIDENAV': {
			return { ...state, miniSidenav: action.value }
		}
		case 'TRANSPARENT_SIDENAV': {
			return { ...state, transparentSidenav: action.value }
		}
		case 'WHITE_SIDENAV': {
			return { ...state, whiteSidenav: action.value }
		}
		case 'SIDENAV_COLOR': {
			return { ...state, sidenavColor: action.value }
		}
		case 'TRANSPARENT_NAVBAR': {
			return { ...state, transparentNavbar: action.value }
		}
		case 'FIXED_NAVBAR': {
			return { ...state, fixedNavbar: action.value }
		}
		case 'LAYOUT': {
			return { ...state, layout: action.value }
		}
		case 'DARKMODE': {
			return { ...state, darkMode: action.value }
		}
		case 'ROUTES': {
			return { ...state, routes: action.value }
		}
		case 'CONFIGURATION_ORGANISATION': {
			return {
				...state,
				configuration: { organisation: action.value, feedback: state.configuration?.feedback },
			}
		}
		case 'CONFIGURATION_FEEDBACK': {
			return {
				...state,
				configuration: { organisation: state.configuration?.organisation, feedback: action.value },
			}
		}
		case 'ORG_MEMBERS': {
			return { ...state, orgMembers: action.value }
		}
		case 'BUSINESS_ACCOUNTS': {
			return { ...state, businessAccounts: action.value }
		}
		case 'ORG_ASSETS': {
			return { ...state, orgAssets: action.value }
		}
		default: {
			throw new Error(`Unhandled action type: ${action.type}`)
		}
	}
}

// Material Dashboard 2 PRO React context provider
function MaterialUIControllerProvider({ children }: { children: ReactNode }): JSX.Element {
	const initialState: StateTypes = {
		//TODO: Don't think we need some state values but deeply integrated (sidenavcolor, transparentNavBar)
		miniSidenav: false,
		transparentSidenav: false,
		whiteSidenav: false,
		sidenavColor: 'primary',
		transparentNavbar: true,
		fixedNavbar: false,
		layout: 'dashboard',
		darkMode: false,
		routes: initRoutes,
		orgAssets: null,
	}

	const [controller, dispatch] = useReducer(reducer, initialState)

	const value = useMemo(
		() => [controller, dispatch] as MaterialUIContextType,
		[controller, dispatch]
	)

	return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>
}

// Material Dashboard 2 PRO React custom hook for using context
function useMaterialUIController() {
	const context = useContext(MaterialUI)

	if (!context) {
		throw new Error(
			'useMaterialUIController should be used inside the MaterialUIControllerProvider.'
		)
	}

	return context
}

// Context module functions
const setMiniSidenav = (
	dispatch: (arg: { type: 'MINI_SIDENAV'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'MINI_SIDENAV', value })

const setTransparentSidenav = (
	dispatch: (arg: { type: 'TRANSPARENT_SIDENAV'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'TRANSPARENT_SIDENAV', value })

const setWhiteSidenav = (
	dispatch: (arg: { type: 'WHITE_SIDENAV'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'WHITE_SIDENAV', value })

const setTransparentNavbar = (
	dispatch: (arg: { type: 'TRANSPARENT_NAVBAR'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'TRANSPARENT_NAVBAR', value })

const setFixedNavbar = (
	dispatch: (arg: { type: 'FIXED_NAVBAR'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'FIXED_NAVBAR', value })

const setOpenConfigurator = (
	dispatch: (arg: { type: 'OPEN_CONFIGURATOR'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'OPEN_CONFIGURATOR', value })

const setLayout = (
	dispatch: (arg: { type: 'LAYOUT'; value: 'dashboard' | 'page' }) => void,
	value: 'dashboard' | 'page'
) => dispatch({ type: 'LAYOUT', value })

const setDarkMode = (
	dispatch: (arg: { type: 'DARKMODE'; value: boolean }) => void,
	value: boolean
) => dispatch({ type: 'DARKMODE', value })

const setOrgMembers = (
	dispatch: (arg: { type: 'ORG_MEMBERS'; value: ShortOrganisationMembers[] }) => void,
	value: ShortOrganisationMembers[]
) => dispatch({ type: 'ORG_MEMBERS', value })

const setBusinessAccounts = (
	dispatch: (arg: { type: 'BUSINESS_ACCOUNTS'; value: ShortBusinessAccounts[] }) => void,
	value: ShortBusinessAccounts[]
) => dispatch({ type: 'BUSINESS_ACCOUNTS', value })

const setConfigurationOrganisation = (
	dispatch: (arg: { type: 'CONFIGURATION_ORGANISATION'; value: OrganisationConfiguration }) => void,
	value: OrganisationConfiguration
) => dispatch({ type: 'CONFIGURATION_ORGANISATION', value })

const setConfigurationFeedback = (
	dispatch: (arg: { type: 'CONFIGURATION_FEEDBACK'; value: FeedbackConfiguration }) => void,
	value: FeedbackConfiguration
) => dispatch({ type: 'CONFIGURATION_FEEDBACK', value })

const updateRoutes = (
	dispatch: (arg: { type: 'ROUTES'; value: RouteType[] }) => void,
	routes: RouteType[],
	newRoutes: RouteType[]
) => {
	const parsedRoutes = newRoutes
		.map(nR => {
			if (routes.some(r => r.key === nR.key)) {
				return null // Skip if route already exists
			} else {
				return nR
			}
		})
		.filter(nR => nR !== null)

	return dispatch({ type: 'ROUTES', value: routes.concat(parsedRoutes) })
}

export const setOrganisationAssets = (
	dispatch: (arg: { type: 'ORG_ASSETS'; value: OrgAssets }) => void,
	value: OrgAssets
) => dispatch({ type: 'ORG_ASSETS', value })

export {
	MaterialUIControllerProvider,
	useMaterialUIController,
	setMiniSidenav,
	setTransparentSidenav,
	setWhiteSidenav,
	setTransparentNavbar,
	setFixedNavbar,
	setOpenConfigurator,
	setLayout,
	setDarkMode,
	setOrgMembers,
	setBusinessAccounts,
	setConfigurationOrganisation,
	setConfigurationFeedback,
	updateRoutes,
}
